import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { noteTakerStore } from 'shared/stores/noteTakerStore'
import { chatWidgetStore } from 'shared/stores/chatWidgetStore'
import { buildKodaContext, stampKodaContext } from 'widgets/ChatWidget/kodaContext'
import { SESSION_DRAWER_STATUS } from 'widgets/NoteTaker/types/sessionDrawer'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import DiscardSummaryModal from './DiscardSummaryModal'
import SessionActionItemsSection from './SessionActionItemsSection'
import SessionConflictBanner from './SessionConflictBanner'
import SessionDrawerHeader from './SessionDrawerHeader'
import SessionNoteCard from './SessionNoteCard'
import SessionSummarySection from './SessionSummarySection'
import SessionTranscriptPanel from './SessionTranscriptPanel'
import s from '../NoteTakerDrawer.module.scss'

const NoteTakerSessionDrawer = observer(() => {
	const showAddedToast = noteTakerStore.showAddedToast
	const isTranscriptOpen = noteTakerStore.isTranscriptDrawerOpen
	const isDrawerOpen = noteTakerStore.isDrawerOpen
	const sessionNote = noteTakerStore.sessionNote
	const student = noteTakerStore.assignedStudent
	const studentName = student?.name || 'Student'
	const sessionNoteId = sessionNote?.id || noteTakerStore.activeSessionNoteId

	useEffect(() => {
		if (!showAddedToast) return undefined
		const timeoutId = window.setTimeout(() => {
			noteTakerStore.dismissAddedToast()
		}, 2600)
		return () => window.clearTimeout(timeoutId)
	}, [showAddedToast])

	// Ambient free-chat context while the drawer is open — not only Ask Koda.
	useEffect(() => {
		if (!isDrawerOpen) return undefined
		chatWidgetStore.setPageContext(
			stampKodaContext(
				buildKodaContext({
					source: 'session_note',
					subject: { studentId: student?.id, studentName },
					entities: { sessionNoteId },
					session: { title: sessionNote?.title, summary: sessionNote?.summary },
				}),
			),
		)
		return () => {
			chatWidgetStore.setPageContext(null)
		}
	}, [
		isDrawerOpen,
		sessionNoteId,
		sessionNote?.title,
		sessionNote?.summary,
		student?.id,
		studentName,
	])

	if (!isDrawerOpen) return null
	const studentGrade = student?.grade ? `Grade ${student.grade}` : 'Grade'
	const topic = sessionNote?.title || noteTakerStore.activeNoteRecord?.topic || 'Session note'
	const drawerStatus = noteTakerStore.sessionDrawerStatus

	return createPortal(
		<>
			<div className={s.overlay} role="dialog" aria-modal="true">
				<div className={s.scrim} onClick={() => noteTakerStore.requestCloseDrawer()} />
				<section
					className={[
						s.surface,
						isTranscriptOpen ? s.surfaceWithTranscript : '',
					]
						.filter(Boolean)
						.join(' ')}
				>
					{showAddedToast ? (
						<div className={s.toast}>
							<span>
								<CheckIcon />
							</span>
							Added to Profile
						</div>
					) : null}

					<SessionDrawerHeader
						studentName={studentName}
						studentGrade={studentGrade}
						topic={topic}
					/>

					<SessionConflictBanner />

					<div
						className={[
							s.surfaceBody,
							isTranscriptOpen ? s.surfaceBodySplit : '',
						]
							.filter(Boolean)
							.join(' ')}
					>
						<SessionTranscriptPanel />

						<div className={s.document}>
							<div className={s.documentInner}>
								{drawerStatus === SESSION_DRAWER_STATUS.LOADING ? (
									<div className={s.drawerState}>Loading session...</div>
								) : null}

								{drawerStatus === SESSION_DRAWER_STATUS.ERROR ? (
									<div className={s.drawerState}>
										<p>
											{noteTakerStore.sessionDrawerError?.message ||
												'Could not load session'}
										</p>
										<button
											type="button"
											onClick={() => noteTakerStore.retryOpenSessionDrawer()}
										>
											Retry
										</button>
										<button
											type="button"
											onClick={() => noteTakerStore.requestCloseDrawer()}
										>
											Close
										</button>
									</div>
								) : null}

								{drawerStatus === SESSION_DRAWER_STATUS.READY && sessionNote ? (
									<>
										<SessionNoteCard
											title={sessionNote.title || 'Session note'}
											createdAt={sessionNote.createdAt}
											durationMs={sessionNote.durationMs}
											sessionNoteId={sessionNote.id}
											audioEnabled
										/>
										<SessionSummarySection />
										<SessionActionItemsSection studentName={studentName} />
									</>
								) : null}
							</div>
						</div>
					</div>
				</section>
			</div>
			<DiscardSummaryModal />
		</>,
		document.body,
	)
})

export default NoteTakerSessionDrawer
